import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FileText, Upload, Download, Sparkles, Image, Trash2, 
  ClipboardPaste, Save, Eye, X, Plus, Camera, Search, Shield
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// INSPECTION REPORT ENGINE - Enhanced
// With Park Dropdown, Job Auto-populate, Insurance Details
// ============================================================

export default function InspectionReportPage() {
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job_id');
  
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [parks, setParks] = useState([]);
  const [parkSearch, setParkSearch] = useState('');
  const [showParkDropdown, setShowParkDropdown] = useState(false);
  
  const [form, setForm] = useState({
    // Location
    site: '',
    location: '',
    property: '',
    postcode: '',
    
    // Reference
    date: '',
    ref: '',
    
    // Personnel
    inspector: '',
    client: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    
    // Insurance Details (NEW)
    insurance_provider: '',
    policy_number: '',
    policyholder_name: '',
    policyholder_address: '',
    claim_reference: '',
    
    // Report Content
    summary: '',
    findings: '',
    scope: '',
    technical: '',
    recommendations: '',
  });
  
  const [images, setImages] = useState([]);
  const [linkedJob, setLinkedJob] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch parks for dropdown
  useEffect(() => {
    const fetchParks = async () => {
      try {
        const res = await axios.get(`${API}/api/parks`, { headers });
        setParks(res.data || []);
      } catch (err) {
        console.error('Failed to load parks:', err);
      }
    };
    fetchParks();
  }, []);

  // Auto-populate from job if job_id provided
  useEffect(() => {
    const fetchJob = async () => {
      if (!jobId) return;
      try {
        const res = await axios.get(`${API}/api/fm/jobs/${jobId}`, { headers });
        const job = res.data;
        setLinkedJob(job);
        
        // Auto-populate fields from job
        setForm(prev => ({
          ...prev,
          site: job.site_name || job.park || prev.site,
          location: job.address || prev.location,
          property: job.plot || job.plot_number || prev.property,
          postcode: job.postcode || prev.postcode,
          client: job.client_name || prev.client,
          contact_name: job.contact_name || prev.contact_name,
          contact_phone: job.contact_phone || prev.contact_phone,
          contact_email: job.contact_email || prev.contact_email,
          ref: `LHS-${job.job_reference || job.id}`,
        }));
        setParkSearch(job.site_name || job.park || '');
        toast.success('Form populated from job details');
      } catch (err) {
        console.error('Failed to load job:', err);
      }
    };
    fetchJob();
  }, [jobId]);

  // Filter parks based on search
  const filteredParks = parks.filter(p => 
    (p.name || '').toLowerCase().includes(parkSearch.toLowerCase()) ||
    (p.address || '').toLowerCase().includes(parkSearch.toLowerCase()) ||
    (p.postcode || '').toLowerCase().includes(parkSearch.toLowerCase())
  );

  // Handle park selection
  const handleParkSelect = (park) => {
    setForm(prev => ({
      ...prev,
      site: park.name,
      location: park.address || prev.location,
      postcode: park.postcode || prev.postcode,
      contact_name: park.contact_name || prev.contact_name,
      contact_phone: park.contact_phone || prev.contact_phone,
      contact_email: park.contact_email || prev.contact_email
    }));
    setParkSearch(park.name);
    setShowParkDropdown(false);
  };

  // -------------------------
  // FIELD EXTRACTION
  // -------------------------
  const extractFields = async () => {
    if (!pasteText.trim()) {
      toast.error('Please paste some text first');
      return;
    }
    
    setExtracting(true);
    try {
      const res = await axios.post(`${API}/api/inspection-reports/extract-fields`, {
        text: pasteText
      }, { headers });
      
      if (res.data.success) {
        const fields = res.data.fields;
        setForm(prev => ({
          ...prev,
          ...fields
        }));
        toast.success('Fields extracted successfully');
      }
    } catch (err) {
      console.error('Extract error:', err);
      toast.error('Failed to extract fields');
    } finally {
      setExtracting(false);
    }
  };

  // -------------------------
  // IMAGE HANDLING
  // -------------------------
  const handleImageUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setImages(prev => [...prev, {
          id: Date.now() + Math.random(),
          data: reader.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
    
    e.target.value = '';
  }, []);

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  // -------------------------
  // GENERATE PDF
  // -------------------------
  const generatePDF = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        images: images.map(img => img.data),
        linked_job_id: linkedJob?.id || linkedJob?.internal_id
      };
      
      const res = await axios.post(`${API}/api/inspection-reports/generate`, payload, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = res.headers['content-disposition'];
      let filename = 'inspection_report.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report generated successfully');
    } catch (err) {
      console.error('Generate error:', err);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  // -------------------------
  // SAVE DRAFT
  // -------------------------
  const saveDraft = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        images: images.map(img => img.data),
        linked_job_id: linkedJob?.id || linkedJob?.internal_id
      };
      
      const res = await axios.post(`${API}/api/inspection-reports/save`, payload, { headers });
      
      if (res.data.success) {
        toast.success(`Report saved (ID: ${res.data.report_id})`);
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save report');
    } finally {
      setLoading(false);
    }
  };

  // -------------------------
  // CLEAR FORM
  // -------------------------
  const clearForm = () => {
    setForm({
      site: '',
      location: '',
      property: '',
      postcode: '',
      date: '',
      ref: '',
      inspector: '',
      client: '',
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      insurance_provider: '',
      policy_number: '',
      policyholder_name: '',
      policyholder_address: '',
      claim_reference: '',
      summary: '',
      findings: '',
      scope: '',
      technical: '',
      recommendations: '',
    });
    setImages([]);
    setPasteText('');
    setParkSearch('');
    setLinkedJob(null);
  };

  // -------------------------
  // UI
  // -------------------------
  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="bg-[#1F7A78] text-white p-6 rounded-2xl mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Inspection Report Engine
            </h1>
            <p className="text-sm opacity-80 mt-1">
              Generate professional inspection reports with photo evidence
            </p>
            {linkedJob && (
              <Badge className="mt-2 bg-white/20">
                Linked to Job: {linkedJob.job_reference || linkedJob.id}
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Button 
              variant="secondary" 
              className="bg-white/20 hover:bg-white/30 text-white"
              onClick={clearForm}
            >
              <X className="h-4 w-4 mr-1" /> Clear
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Quick Extract Panel */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ClipboardPaste className="h-4 w-4 text-[#1F7A78]" />
                Quick Extract
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Paste inspection notes or report text here to auto-extract fields..."
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                className="min-h-[120px] mb-3"
              />
              <Button 
                onClick={extractFields} 
                disabled={extracting || !pasteText.trim()}
                className="bg-[#1F7A78] hover:bg-[#16615f]"
              >
                {extracting ? (
                  <><Sparkles className="h-4 w-4 mr-2 animate-spin" /> Extracting...</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" /> Extract Fields</>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Location Details - With Park Dropdown */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Location Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Park Dropdown Search */}
                <div className="relative sm:col-span-2">
                  <Label>Park / Site</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={parkSearch}
                      onChange={(e) => {
                        setParkSearch(e.target.value);
                        setForm({...form, site: e.target.value});
                        setShowParkDropdown(true);
                      }}
                      onFocus={() => setShowParkDropdown(true)}
                      placeholder="Search parks..."
                      className="pl-10"
                    />
                  </div>
                  
                  {/* Dropdown Results */}
                  {showParkDropdown && parkSearch && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredParks.length === 0 ? (
                        <div className="p-3 text-sm text-slate-500">
                          No parks found - type name to create new
                        </div>
                      ) : (
                        filteredParks.slice(0, 10).map(park => (
                          <button
                            key={park.id}
                            onClick={() => handleParkSelect(park)}
                            className="w-full text-left p-3 hover:bg-slate-50 border-b last:border-0"
                          >
                            <p className="font-medium text-slate-800">{park.name}</p>
                            <p className="text-xs text-slate-500">
                              {park.address}{park.postcode && ` • ${park.postcode}`}
                            </p>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                
                <div>
                  <Label>Property / Plot</Label>
                  <Input
                    placeholder="Plot number"
                    value={form.property}
                    onChange={(e) => setForm({...form, property: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Postcode</Label>
                  <Input
                    placeholder="e.g. SS1 1AA"
                    value={form.postcode}
                    onChange={(e) => setForm({...form, postcode: e.target.value})}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Full Address</Label>
                  <Input
                    placeholder="Address / location"
                    value={form.location}
                    onChange={(e) => setForm({...form, location: e.target.value})}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Client & Inspector Details */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Client & Inspector Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Client Name</Label>
                  <Input
                    placeholder="Client / Company name"
                    value={form.client}
                    onChange={(e) => setForm({...form, client: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    placeholder="Contact person"
                    value={form.contact_name}
                    onChange={(e) => setForm({...form, contact_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    placeholder="Phone number"
                    value={form.contact_phone}
                    onChange={(e) => setForm({...form, contact_phone: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={form.contact_email}
                    onChange={(e) => setForm({...form, contact_email: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Inspector Name</Label>
                  <Input
                    placeholder="Inspector name"
                    value={form.inspector}
                    onChange={(e) => setForm({...form, inspector: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Date of Inspection</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({...form, date: e.target.value})}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Report Reference</Label>
                  <Input
                    placeholder="Report reference"
                    value={form.ref}
                    onChange={(e) => setForm({...form, ref: e.target.value})}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Insurance Details (NEW) */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-4 w-4 text-[#1F7A78]" />
                Insurance Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Insurance Provider</Label>
                  <Input
                    placeholder="Insurance company name"
                    value={form.insurance_provider}
                    onChange={(e) => setForm({...form, insurance_provider: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Policy Number</Label>
                  <Input
                    placeholder="Policy number"
                    value={form.policy_number}
                    onChange={(e) => setForm({...form, policy_number: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Policyholder Name</Label>
                  <Input
                    placeholder="Policyholder name"
                    value={form.policyholder_name}
                    onChange={(e) => setForm({...form, policyholder_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Claim Reference</Label>
                  <Input
                    placeholder="Insurance claim reference"
                    value={form.claim_reference}
                    onChange={(e) => setForm({...form, claim_reference: e.target.value})}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Policyholder Address</Label>
                  <Input
                    placeholder="Policyholder address"
                    value={form.policyholder_address}
                    onChange={(e) => setForm({...form, policyholder_address: e.target.value})}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Report Sections */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Report Content</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="summary" className="w-full">
                <TabsList className="grid grid-cols-5 mb-4">
                  <TabsTrigger value="summary">Summary</TabsTrigger>
                  <TabsTrigger value="findings">Findings</TabsTrigger>
                  <TabsTrigger value="scope">Scope</TabsTrigger>
                  <TabsTrigger value="technical">Technical</TabsTrigger>
                  <TabsTrigger value="recommendations">Recs</TabsTrigger>
                </TabsList>
                
                <TabsContent value="summary">
                  <Label>Executive Summary</Label>
                  <Textarea
                    placeholder="Brief overview of the inspection and key findings..."
                    value={form.summary}
                    onChange={(e) => setForm({...form, summary: e.target.value})}
                    className="min-h-[200px]"
                  />
                </TabsContent>
                
                <TabsContent value="findings">
                  <Label>Findings</Label>
                  <Textarea
                    placeholder="Detailed findings from the inspection..."
                    value={form.findings}
                    onChange={(e) => setForm({...form, findings: e.target.value})}
                    className="min-h-[200px]"
                  />
                </TabsContent>
                
                <TabsContent value="scope">
                  <Label>Scope of Works</Label>
                  <Textarea
                    placeholder="Required remedial works and scope..."
                    value={form.scope}
                    onChange={(e) => setForm({...form, scope: e.target.value})}
                    className="min-h-[200px]"
                  />
                </TabsContent>
                
                <TabsContent value="technical">
                  <Label>Technical Opinion</Label>
                  <Textarea
                    placeholder="Technical assessment and professional opinion..."
                    value={form.technical}
                    onChange={(e) => setForm({...form, technical: e.target.value})}
                    className="min-h-[200px]"
                  />
                </TabsContent>
                
                <TabsContent value="recommendations">
                  <Label>Recommendations</Label>
                  <Textarea
                    placeholder="Recommended actions and priority..."
                    value={form.recommendations}
                    onChange={(e) => setForm({...form, recommendations: e.target.value})}
                    className="min-h-[200px]"
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Photos & Actions */}
        <div className="space-y-6">
          
          {/* Photo Upload */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Camera className="h-4 w-4 text-[#1F7A78]" />
                Photo Evidence
                {images.length > 0 && (
                  <Badge className="ml-auto bg-[#1F7A78]">{images.length}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-[#1F7A78] transition-colors cursor-pointer"
                onClick={() => document.getElementById('photo-upload').click()}
              >
                <input
                  id="photo-upload"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-600">
                  Click to upload photos
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  JPG, PNG up to 10MB each
                </p>
              </div>
              
              {/* Image Previews */}
              {images.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {images.map((img) => (
                    <div key={img.id} className="relative group">
                      <img
                        src={img.data}
                        alt={img.name}
                        className="w-full h-24 object-cover rounded-lg border"
                      />
                      <button
                        onClick={() => removeImage(img.id)}
                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <p className="text-xs text-slate-500 truncate mt-1">{img.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={generatePDF}
                disabled={loading}
                className="w-full bg-[#1F7A78] hover:bg-[#16615f]"
              >
                {loading ? (
                  <><Download className="h-4 w-4 mr-2 animate-bounce" /> Generating...</>
                ) : (
                  <><Download className="h-4 w-4 mr-2" /> Generate PDF</>
                )}
              </Button>
              
              <Button
                onClick={saveDraft}
                disabled={loading}
                variant="outline"
                className="w-full"
              >
                <Save className="h-4 w-4 mr-2" /> Save Draft
              </Button>
              
              <div className="pt-2 border-t">
                <p className="text-xs text-slate-500 text-center">
                  Report will include all photos and formatted content
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardContent className="pt-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Photos</span>
                  <span className="font-medium">{images.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Fields filled</span>
                  <span className="font-medium">
                    {Object.values(form).filter(v => v && v.toString().trim()).length}/{Object.keys(form).length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Status</span>
                  <Badge variant="outline">Draft</Badge>
                </div>
                {linkedJob && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Linked Job</span>
                    <Badge className="bg-[#1F7A78]">{linkedJob.job_reference}</Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
